/**
 * MockMemoriesStore — web mirror of `Sources/Store/MockMemoriesStore.swift`.
 *
 * In-memory memories store with realistic sample data so the Brain tab renders
 * standalone in MOCK mode — the same way MockFactoryStore backs the Factory tab.
 * Filtering (`type`/`workspace`), keyword search, and `limit` are applied locally
 * to mirror the Worker's `GET /memories` semantics.
 */

import type { Memory, MemorySummary, MemoryType } from '../models/memory';
import { memoryType } from '../models/memory';
import type { MemoriesStore } from './MemoriesStore';
import { MemoriesStoreError } from './MemoriesStore';

const MOCK_LATENCY_MS = 250;

export class MockMemoriesStore implements MemoriesStore {
  private readonly memories: Memory[] = seed();

  private async delay(): Promise<void> {
    await new Promise((r) => setTimeout(r, MOCK_LATENCY_MS));
  }

  async list(opts?: { workspace?: string; type?: MemoryType; limit?: number }): Promise<MemorySummary[]> {
    await this.delay();
    return this.apply(opts?.workspace, opts?.type, opts?.limit);
  }

  async get(id: string): Promise<Memory> {
    await this.delay();
    const m = this.memories.find((x) => x.id === id);
    if (!m) throw new MemoriesStoreError(`no memory ${id}`, 404);
    return m;
  }

  async search(opts: {
    query: string;
    workspace?: string;
    type?: MemoryType;
    limit?: number;
  }): Promise<MemorySummary[]> {
    await this.delay();
    const q = opts.query.trim();
    return this.apply(opts.workspace, opts.type, opts.limit, q.length ? q : undefined);
  }

  /** Filter by workspace/type, keyword-match name/description/content (ILIKE-ish),
   * sort by updated desc, clamp limit — mirrors the Worker + the Swift mock. */
  private apply(
    workspace?: string,
    type?: MemoryType,
    limit?: number,
    matching?: string,
  ): MemorySummary[] {
    let rows = this.memories;
    if (workspace) rows = rows.filter((m) => m.workspace === workspace);
    if (type) rows = rows.filter((m) => memoryType(m.type) === type);
    if (matching) {
      const needle = matching.toLowerCase();
      rows = rows.filter(
        (m) =>
          m.name.toLowerCase().includes(needle) ||
          (m.description ?? '').toLowerCase().includes(needle) ||
          (m.content ?? '').toLowerCase().includes(needle),
      );
    }
    const sorted = [...rows].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
    const capped = Math.min(Math.max(limit ?? 100, 1), 500);
    return sorted.slice(0, capped).map(toSummary);
  }
}

function toSummary(m: Memory): MemorySummary {
  return {
    id: m.id,
    name: m.name,
    type: m.type,
    description: m.description,
    workspace: m.workspace,
    updatedAt: m.updatedAt,
  };
}

// MARK: - Seed data (ported from MockMemories.seed)

function seed(): Memory[] {
  const ws = 'sully-personal';
  const trendy = 'trendy-video';

  return [
    {
      id: 'mem-user-0001',
      name: 'user_preferences',
      type: 'user',
      description: "Sully's working style: async orchestrator, dual-channel notifications, action over asking.",
      workspace: ws,
      updatedAt: '2026-07-18T21:14:00Z',
      createdAt: '2026-05-02T09:00:00Z',
      content: `# User Preferences

- Default to **background subagents** so the main thread stays free to chat.
- Send BOTH notifications (Jarvis desktop + ntfy phone) on significant work.
- Prefer DOING over explaining — use full computer/browser control.
- Keep responses concise; prefer action over asking permission for routine ops.
- Use git worktrees to run parallel sessions.`,
      frontmatter: { pinned: true, channels: ['jarvis', 'ntfy'] },
    },
    {
      id: 'mem-user-0002',
      name: 'user_new_job_july2026',
      type: 'user',
      description: 'New job Jul 2026; mini = dedicated agent host, M4 Air is the daily machine.',
      workspace: ws,
      updatedAt: '2026-07-15T13:02:00Z',
      createdAt: '2026-07-01T08:00:00Z',
      content: `# New Job — July 2026

Steer the dedicated agent host (the mini) via Discord. The M4 Air stays the
interactive machine. Keep the fleet paced at 3–4 agents (~1.4M tok/min) to
stay under the 5-hour plan cap.`,
      frontmatter: { machine: 'mac-mini', control: 'discord' },
    },
    {
      id: 'mem-feedback-0001',
      name: 'feedback_no_em_dashes_user_facing',
      type: 'feedback',
      description: 'No em dashes in user-facing copy.',
      workspace: ws,
      updatedAt: '2026-07-17T10:41:00Z',
      createdAt: '2026-06-10T11:00:00Z',
      content: `# Feedback: user-facing copy

Do not use em dashes in anything a user reads. Use periods or commas. This
applies to emails, UI strings, and generated marketing copy.`,
      frontmatter: { severity: 'style', scope: 'user-facing' },
    },
    {
      id: 'mem-feedback-0002',
      name: 'feedback_no_vercel',
      type: 'feedback',
      description: 'Trendy deploys on Hetzner, NOT Vercel. Verify with the browser.',
      workspace: trendy,
      updatedAt: '2026-07-16T18:20:00Z',
      createdAt: '2026-05-20T09:30:00Z',
      content: `# Feedback: deploy target

Trendy Video production is Docker Compose on Hetzner behind a self-hosted
webhook — **not** Vercel and **not** GitHub Actions. Always verify a deploy
by driving the live app in the browser, then send both notifications.`,
      frontmatter: { infra: 'hetzner', verify: 'browser' },
    },
    {
      id: 'mem-project-0001',
      name: 'project_brain_multi_user_workspaces',
      type: 'project',
      description: 'Brain MCP multi-user workspaces — P0 + P1 done; memories REST API just merged.',
      workspace: ws,
      updatedAt: '2026-07-19T02:05:00Z',
      createdAt: '2026-06-28T14:00:00Z',
      content: `# Brain Multi-User Workspaces

Phases 0 and 1 shipped: per-user \`brain_*\` tokens, workspace memberships,
RLS-scoped factory read-model. Latest: a read-only **memories REST API**
(\`/memories\`, \`/memories/:id\`, \`/memories/search\`) for the app Brain tab,
authed with the same \`brain_*\` token — no Supabase JWT.

Next: wire the Brain tab in both clients (native + web) to this API.`,
      frontmatter: {
        status: 'in-progress',
        phase: 'P1',
        endpoints: ['/memories', '/memories/:id', '/memories/search'],
      },
    },
    {
      id: 'mem-project-0002',
      name: 'project_revenue_autopilot_jul10',
      type: 'project',
      description: 'Revenue pivot: 39 signups/30d, 0 return, $0 MRR. Activation P0 + winback in flight.',
      workspace: trendy,
      updatedAt: '2026-07-18T07:30:00Z',
      createdAt: '2026-07-10T09:00:00Z',
      content: `# Revenue Autopilot

Signals: 39 signups in 30 days, zero returning, $0 MRR. Mandate is to
optimize PRODUCT metrics (activation, return), not queue health.

In flight: activation P0, a winback drip campaign, and an onboarding
tour fix for the \`generating\` hang.`,
      frontmatter: { mrr: 0, signups_30d: 39, priority: 'P0' },
    },
    {
      id: 'mem-project-0003',
      name: 'project_nouri_testflight_delivery',
      type: 'project',
      description: 'Nouri TestFlight delivery loop: ASC app 6792368176, bundle com.sullyk.nouriai.',
      workspace: ws,
      updatedAt: '2026-07-17T22:12:00Z',
      createdAt: '2026-07-05T10:00:00Z',
      content: `# Nouri TestFlight Delivery

App Store Connect app id 6792368176, bundle \`com.sullyk.nouriai\`, API key
5N472K47DD. Build 9 (native TabView → automatic iOS 26 Liquid Glass) is up.
The delivery-blackhole saga is documented; the automated finish chain
archives, exports, uploads, and answers export compliance.`,
      frontmatter: { bundle: 'com.sullyk.nouriai', build: 9 },
    },
    {
      id: 'mem-reference-0001',
      name: 'reference_prod_deploy_webhook',
      type: 'reference',
      description: 'Prod deploys via a self-hosted webhook, not GitHub Actions.',
      workspace: trendy,
      updatedAt: '2026-07-14T16:45:00Z',
      createdAt: '2026-04-30T12:00:00Z',
      content: `# Reference: Production Deploy Webhook

\`main\` push → self-hosted webhook → SSH to the Hetzner host → git pull +
submodule update + docker compose build/up for the changed service. NOT
GitHub Actions. Stale-container fix: \`docker rm -f <name>\` then
\`up -d --force-recreate <service>\`.`,
      frontmatter: { host: 'hetzner', trigger: 'webhook' },
    },
    {
      id: 'mem-reference-0002',
      name: 'reference_brain_mcp_infra',
      type: 'reference',
      description: 'Brain MCP infra: Cloudflare Worker + Supabase project qmttigbqvpgzpfmdmktm.',
      workspace: ws,
      updatedAt: '2026-07-19T01:10:00Z',
      createdAt: '2026-06-29T09:15:00Z',
      content: `# Reference: Brain MCP Infra

Worker: \`claude-brain.suleimankhan11.workers.dev\`. Supabase project
\`qmttigbqvpgzpfmdmktm\`. Auth: \`/login\` → durable \`brain_*\` bearer;
\`/supabase-token\` mints a 1h HS256 Supabase JWT carrying \`tenant_id\`.
The memories API is mounted OUTSIDE the OAuth \`/mcp\` route and does its
own \`brain_*\` Bearer auth.`,
      frontmatter: {
        worker: 'claude-brain.suleimankhan11.workers.dev',
        supabase: 'qmttigbqvpgzpfmdmktm',
      },
    },
    {
      id: 'mem-index-0001',
      name: 'MEMORY',
      type: 'index',
      description: 'Top-level index of active projects, references, and feedback memories.',
      workspace: ws,
      updatedAt: '2026-07-19T02:30:00Z',
      createdAt: '2026-04-01T08:00:00Z',
      content: `# Project Memory — Index

- Active: brain multi-user workspaces, revenue autopilot, nouri testflight.
- References: prod deploy webhook, brain MCP infra, drive folder ids.
- Feedback: no em dashes, no Vercel, all-approvals-until-revenue.

Read the relevant project/reference memory FIRST when resuming a loop.`,
      frontmatter: { kind: 'index', pinned: true },
    },
  ];
}
